
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { ProfileSidebar } from "@/components/profile/ProfileSidebar";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { Loader2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

const MyReviews = () => {
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const reviewsPerPage = 5;
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserReviews = async () => {
      try {
        setLoading(true);
        
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/auth');
          return;
        }
        
        const userId = session.user.id;
        
        // Get total count for pagination
        const { count, error: countError } = await supabase
          .from('reviews')
          .select('*', { count: 'exact', head: true })
          .eq('customer_id', userId);
          
        if (countError) {
          console.error('Error getting reviews count:', countError);
          return;
        }
        
        setTotalPages(Math.ceil((count || 0) / reviewsPerPage));
        
        // Fetch reviews with service information
        const { data: reviewsData, error } = await supabase
          .from('reviews')
          .select(`
            id,
            rating,
            comment,
            created_at,
            customer_id,
            service_id,
            services:service_id (
              id,
              name,
              image_url
            )
          `)
          .eq('customer_id', userId)
          .order('created_at', { ascending: false })
          .range((currentPage - 1) * reviewsPerPage, currentPage * reviewsPerPage - 1);
        
        if (error) {
          console.error('Error fetching reviews:', error);
          setReviews([]);
        } else {
          console.log('Fetched reviews:', reviewsData);
          setReviews(reviewsData || []);
        }
      } catch (error) {
        console.error('Error in fetchUserReviews:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserReviews();
  }, [navigate, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-gray-50 flex flex-col w-full">
        {/* Fixed header */}
        <div className="fixed top-0 left-0 right-0 z-40 h-16">
          <Header showSearchAndLocation={false} />
        </div>
        
        {/* Main layout with sidebar - starts after header */}
        <div className="flex flex-1 pt-16">
          <ProfileSidebar />
          
          <SidebarInset>
            {/* Main content with proper spacing to clear header */}
            <main className="flex-1 overflow-auto p-4 md:p-6">
              <div className="max-w-4xl mx-auto">
                <h1 className="text-3xl font-bold mb-4">My Reviews</h1>
                
                {loading ? (
                  <div className="flex justify-center py-10">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : reviews.length === 0 ? (
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-center text-muted-foreground py-4">
                        You haven't written any reviews yet.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="pt-6">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Service</TableHead>
                            <TableHead>Rating</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {reviews.map((review) => (
                            <TableRow key={review.id} className="cursor-pointer" onClick={() => navigate(`/service/${review.service_id}`)}>
                              <TableCell className="font-medium">
                                <div className="flex items-center gap-3">
                                  {review.services?.image_url && (
                                    <div className="h-10 w-10 rounded-md overflow-hidden">
                                      <img 
                                        src={review.services.image_url} 
                                        alt={review.services?.name} 
                                        className="h-full w-full object-cover"
                                      />
                                    </div>
                                  )}
                                  <span>{review.services?.name || "Unknown Service"}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center">
                                  {[...Array(5)].map((_, i) => (
                                    <svg
                                      key={i}
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 24 24"
                                      fill={i < review.rating ? "currentColor" : "none"}
                                      stroke={i < review.rating ? "none" : "currentColor"}
                                      className={`h-4 w-4 ${i < review.rating ? "text-yellow-400" : "text-gray-300"}`}
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                    </svg>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell>
                                {new Date(review.created_at).toLocaleDateString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      
                      {totalPages > 1 && (
                        <Pagination className="mt-4">
                          <PaginationContent>
                            {currentPage > 1 && (
                              <PaginationItem>
                                <PaginationPrevious 
                                  onClick={() => handlePageChange(currentPage - 1)}
                                  className="cursor-pointer"
                                />
                              </PaginationItem>
                            )}
                            
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                              <PaginationItem key={page}>
                                <PaginationLink
                                  isActive={page === currentPage}
                                  onClick={() => handlePageChange(page)}
                                  className="cursor-pointer"
                                >
                                  {page}
                                </PaginationLink>
                              </PaginationItem>
                            ))}
                            
                            {currentPage < totalPages && (
                              <PaginationItem>
                                <PaginationNext 
                                  onClick={() => handlePageChange(currentPage + 1)}
                                  className="cursor-pointer"
                                />
                              </PaginationItem>
                            )}
                          </PaginationContent>
                        </Pagination>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            </main>
          </SidebarInset>
        </div>
        
        <Footer />
      </div>
    </SidebarProvider>
  );
};

export default MyReviews;
